import { ClassAttributes, HTMLAttributes, ReactChild } from "react";

import Link from "next/link";

import { JSX } from "@emotion/react/jsx-runtime";
import { Anchor, ContentCard as AngelouContentCard } from "@newsela/angelou";
import { withTrackEvent } from "@newsela/snitch";

import {
  contentCardContainerCss,
  contentCardLinkCss,
} from "@/search/NewSearchPageMVP/SearchResults/ContentCard/ContentCard.styles";
import { ContentCardBottomSection } from "@/search/NewSearchPageMVP/SearchResults/ContentCard/ContentCardBottomSection/ContentCardBottomSection";
import { ContentCardTopSection } from "@/search/NewSearchPageMVP/SearchResults/ContentCard/ContentCardTopSection/ContentCardTopSection";
import { ISearchResult } from "@/search/NewSearchPageMVP/types/ISearchResult";
import formatHrefToSupportLegacyLink from "@/utils/formatHrefToSupportLegacyLink";
import { AgathaSchemaParamsEnum } from "../../types/IParametersSchema";
import { IContentSettingsGroup } from "../../types/IContentSettingsGroup";
import { useSearchPageContext } from "../../provider/SearchPageProvider";

// This type is temporary, until the new (and typed) ContentCards are deployed
type PlaceholderContentCardProps = typeof AngelouContentCard;
const ContentCardWithTrackEvent =
  withTrackEvent<PlaceholderContentCardProps>(AngelouContentCard);

const ContentCard = ({
  content,
  dataQaSelector,
  isBreakingNews,
  showContentType = true,
  contentSettingsGroups,
}: IContentCard) => {
  const {
    getters: { searchResponse, validQueryParams },
    handlers: { getSearchParamValues },
  } = useSearchPageContext();

  const contentIds = searchResponse?.results.map((result) => result.content_id);
  const rawQueryString = Object.values(validQueryParams).map(
    (parameter) => parameter?.search_param,
  );

  const totalResultCount = searchResponse?.total_result_count.value;

  return (
    <ContentCardWithTrackEvent
      streamProperties={{
        query_term: getSearchParamValues(AgathaSchemaParamsEnum.needle),
        action_prefix: null,
        action_name: "Search",
        application: "agatha",
        request_type: "search",
        current_url: location?.href,
        description: "Search Results",
        result_identifiers: contentIds,
        user_agent: navigator.userAgent,
        component_type: "ResultListContainer",
        raw_query_string: rawQueryString,
        content_results_retrieved: searchResponse?.results.length || 0,
        count_results_total: totalResultCount || 0,
      }}
      dataProps={{
        "data-testid": `search-page-beta-${content.content_type}`,
        "data-qa-selector": `search-page-beta-${content.content_type} ${dataQaSelector || ""}`,
        onContextMenu: (e: React.MouseEvent) => {
          if (e.button === 2) {
            if ("heap" in globalThis) {
              // This function will be removed after gathering data for a short amount of time.
              // @ts-expect-error This is because we don't type all of globalThis/window, which is not a priority at the moment
              const heapTrack = globalThis.heap.track as (
                eventName: string,
                properties?: Record<string, unknown>,
              ) => void;
              heapTrack("right click search content card", {
                link: content.url,
                betaCard: "true",
              });
            }
          }
        },
        onAuxClick: (e: React.MouseEvent) => {
          if (e.button === 1) {
            if ("heap" in globalThis) {
              // This whole function will be removed after gathering data for a short amount of time.
              // @ts-expect-error This is because we dont type all of globalThis/window, which is not a priority at the moment
              const heapTrack = globalThis.heap.track as (
                eventName: string,
                properties?: Record<string, unknown>,
              ) => void;
              heapTrack("aux click search content card", {
                link: content.url,
                betaCard: "true",
              });
            }
          }
        },
      }}
      __renderCustomLink={() => (
        <Link
          href={formatHrefToSupportLegacyLink(content.url || "")}
          passHref
          legacyBehavior
        >
          <Anchor
            __cssFor={contentCardLinkCss}
            ariaProps={{
              "aria-label": `View "${content?.title || ""}..."`,
            }}
          />
        </Link>
      )}
      __renderCustomWrapper={(
        cardProps: JSX.IntrinsicAttributes &
          ClassAttributes<HTMLElement> &
          HTMLAttributes<HTMLElement>,
        linkTagWithChildren: ReactChild,
      ) => (
        <article {...cardProps} css={contentCardContainerCss}>
          {linkTagWithChildren}
        </article>
      )}
      __renderImage={() => (
        <ContentCardTopSection
          content={content}
          isBreakingNews={isBreakingNews}
          showContentType={showContentType}
        />
      )}
      __renderTextual={() => (
        <ContentCardBottomSection
          content={content}
          contentSettingsGroups={contentSettingsGroups}
        />
      )}
    />
  );
};

interface IContentCard {
  source?: string;
  content: ISearchResult;
  dataQaSelector?: string;
  isFormative?: boolean | undefined;
  isBreakingNews?: boolean | undefined;
  showContentType?: boolean;
  contentSettingsGroups: IContentSettingsGroup[];
}

export { ContentCard };
